import { Component, OnDestroy, OnInit, Input, signal } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { Observable, Subject, timer } from 'rxjs';
import { map, takeWhile, tap, takeUntil } from 'rxjs/operators';
import { TNPS_FEEDBACK_QUESTION, TNPS_USER_RESPONSE, TREQUEST_NPS_SUBMIT_FORM } from '@monorepo/types';

type TNpsFormViewState = {
  enableSubmit: boolean;
  isSelectionMade: boolean;
  userFeedback: Partial<TNPS_USER_RESPONSE> | null;
};

@Component({
  selector: 'monorepo-nps-form',
  templateUrl: './nps-form.component.html',
  styleUrls: ['./nps-form.component.scss'],
})
export class NpsFormComponent implements OnInit, OnDestroy {
  @Input() timerForEnablingSubmit: number = 500;
  @Input() timerValue: number = 10;
  @Input() feedbackQuestion: TNPS_FEEDBACK_QUESTION = {
    question: '',
    order_no: 1,
    answer_type: 'Emoji',
    detractor_questions: [],
    promoters_questions: [],
  };

  timerValue$: Observable<number> | undefined;
  view = signal<TNpsFormViewState>({
    enableSubmit: false,
    isSelectionMade: false,
    userFeedback: null,
  });
  private stop$ = new Subject<void>();

  constructor(private modalControl: ModalController) {}

  ngOnInit() {
    if (this.feedbackQuestion.answer_type === 'Emoji') {
      this.startCountdownTimer();
    } else {
      this.view.update((v) => ({ ...v, enableSubmit: true }));
    }
  }

  startCountdownTimer() {
    this.timerValue$ = timer(0, 1000).pipe(
      map((i) => this.timerValue - i),
      takeWhile((val) => val >= 0),
      tap((val) => {
        if (val === 0) {
          this.view.update((v) => ({ ...v, isSelectionMade: true }));
          this.skipFeedback('Timeout');
        }
      }),
      takeUntil(this.stop$),
    );
  }

  onSelectionMade() {
    if (!this.view().isSelectionMade) {
      this.view.update((v) => ({ ...v, isSelectionMade: true }));
      this.stop$.next();
      setTimeout(() => {
        this.view.update((v) => ({ ...v, enableSubmit: true }));
      }, this.timerForEnablingSubmit);
    }
  }

  onFeedbackChange(feedback: Partial<TNPS_USER_RESPONSE>) {
    this.view.update((v) => ({ ...v, userFeedback: feedback }));
  }

  submitFeedback() {
    if (!this.view().userFeedback?.answer) {
      return;
    }
    const submitData: TNPS_USER_RESPONSE[] = [
      {
        question: this.feedbackQuestion.question,
        answer_type: this.feedbackQuestion.answer_type,
        answer: this.view().userFeedback?.answer || '',
        selected_options: this.view().userFeedback?.selected_options || [],
      },
    ];
    this.modalControl.dismiss({ action: 'Submit', data: submitData });
  }

  skipFeedback(actionTaken: TREQUEST_NPS_SUBMIT_FORM['user_action'] = 'Skip') {
    this.modalControl.dismiss({ action: actionTaken, data: [] });
  }

  ngOnDestroy() {
    this.stop$.next();
    this.stop$.complete();
  }
}
